import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { AssignorBillingAddressData, isBillingAddressComplete } from '../../components/form/AssignorBillingAddress';

const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

interface UseContractActionsParams {
    apiUrl: string;
    apiKey: string;
    creditOperationId?: string;
    setLoading: (value: boolean) => void;
    hasUnsavedChanges?: boolean;
    hasInvalidDueDates?: boolean;
    validateAllFields?: () => boolean;
    setIsCancelModalOpen: (value: boolean) => void;
    setIsFinishModalOpen: (value: boolean) => void;
    onRefresh?: () => void;
    contractStatus?: string;
    billingAddress?: AssignorBillingAddressData | null;
    requiresBillingAddress?: boolean;
}

export const useContractActions = ({
    apiUrl,
    apiKey,
    creditOperationId,
    setLoading,
    hasUnsavedChanges = false,
    hasInvalidDueDates = false,
    validateAllFields,
    setIsCancelModalOpen,
    setIsFinishModalOpen,
    onRefresh,
    contractStatus,
    billingAddress,
    requiresBillingAddress = false,
}: UseContractActionsParams) => {
    const navigate = useNavigate();

    const handleCancelContract = useCallback(async () => {
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/${creditOperationId}/status/CANCELED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato cancelado com sucesso!');
                navigate(CONTRACTS_LIST_URL);
            } else {
                toast.error('Erro ao cancelar contrato');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            setIsCancelModalOpen(false);
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, setIsCancelModalOpen, navigate]);

    const handleFinishContract = useCallback(async () => {
        if (hasUnsavedChanges) {
            toast.error('Existem alterações não salvas. Salve antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        if (hasInvalidDueDates) {
            toast.error('Existem recebíveis sem data de vencimento. Corrija antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        if (validateAllFields && !validateAllFields()) {
            setIsFinishModalOpen(false);
            return;
        }

        if (requiresBillingAddress && !isBillingAddressComplete(billingAddress)) {
            toast.error('Preencha o endereço de faturamento completo antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/${creditOperationId}/status/SUCCESSFULLY_COMPLETED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato finalizado com sucesso!');
                navigate(CONTRACTS_LIST_URL);
            } else {
                toast.error('Erro ao finalizar contrato');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            setIsFinishModalOpen(false);
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, hasUnsavedChanges, hasInvalidDueDates, validateAllFields, setIsFinishModalOpen, navigate, billingAddress, requiresBillingAddress]);

    const handleBillingProcess = useCallback(async () => {
        if (!creditOperationId) {
            toast.error('Contrato não identificado');
            return;
        }
        if (hasUnsavedChanges) {
            toast.error('Existem alterações não salvas. Salve o contrato antes de faturar.');
            return;
        }
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/billing/process`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ credit_operation_id: creditOperationId })
            });
            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                if (data.message?.includes('Faturamento iniciado')) {
                    toast.warning('Faturamento solicitado com sucesso. Aguarde o processamento');
                } else {
                    toast.error(data.message || 'Erro ao processar faturamento');
                }
            } else {
                const data = await response.json().catch(() => ({}));
                toast.error(data.message || 'Erro ao processar faturamento');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            onRefresh?.();
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, onRefresh, hasUnsavedChanges]);

    const handleRequestRegister = useCallback(async () => {
        if (!creditOperationId) {
            toast.error('Contrato não identificado');
            return;
        }
        if (contractStatus !== 'Finalizado com sucesso') {
            toast.error('A operação precisa estar finalizada com sucesso para solicitar o registro.');
            return;
        }
        if (requiresBillingAddress && !isBillingAddressComplete(billingAddress)) {
            toast.error('Preencha o endereço de faturamento completo antes de solicitar o registro.');
            return;
        }
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/operations/${creditOperationId}/request-register`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || '',
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                toast.success('Registro solicitado com sucesso. Aguarde o processamento.');
            } else {
                const data = await response.json().catch(() => ({}));
                toast.error(data.message || 'Erro ao solicitar registro do contrato');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            onRefresh?.();
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, onRefresh, contractStatus, billingAddress, requiresBillingAddress]);

    return { handleCancelContract, handleFinishContract, handleBillingProcess, handleRequestRegister };
};
